const DEFAULT_NUMBER_SETTINGS = Object.freeze({
  decimalPlaces: 2,
  decimalPoint: ',',
  thousandSeparator: '.',
  thousandPlaces: 3,
  currencySymbol: 'R$',
});

const DEFAULT_DATE_SETTINGS = Object.freeze({
  l18n: 'd/m/Y',
  placeholder: 'dd/mm/aaaa',
});

const escapeRegExp = (value) => String(value ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Classe para formatação de dados 
 * 
 * @author Wellington Pereira
 * @since 2014-03-11
 * @version 7.0.0
 */
export default class Format {

  /**
   * Formata uma data no padrão definido pela configuração
   * 
   * @param {string} date
   * @return {string}
   */
  date(input) {
    if (!input || (!(input instanceof Date) && typeof input !== 'string') || input === '') {
      return null;
    }

    let dateObj;

    if (input instanceof Date) {
      dateObj = input;
    } else {
      // Trunca string se necessário
      const cleanInput = input.length > 19 ? input.split('T')[0] : input;
      dateObj = new Date(`${cleanInput}T00:00:00`);
    }

    if (isNaN(dateObj)) return null;

    const pad = (n) => String(n).padStart(2, '0');

    const day = pad(dateObj.getDate());
    const month = pad(dateObj.getMonth() + 1);
    const year = dateObj.getFullYear().toString();
    const shortYear = year.slice(2);

    const dateDic = globalThis?.dic?.date?.mask || {};
    const format = dateDic.l18n || DEFAULT_DATE_SETTINGS.l18n; // Ex: 'd/m/Y'
    return format
      .replace('d', day)
      .replace('m', month)
      .replace('y', shortYear)
      .replace('Y', year);
  }

  /**
   * Formata uma data no padrão data hora
   * 
   * @param {string} date
   * @param {boolean} [seconds=false] Define se os segundos serão exibidos
   * @return {string}
   */
  dateTime(date, seconds = false) {
    if (!date || (!(date instanceof Date) && typeof date !== 'string') || (typeof date === 'string' && date.length === 0)) {
      return 'NULL';
    }

    if (date && date instanceof Date) {
      const year = String(date.getFullYear());
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes());
      const second = String(date.getSeconds());
      date = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
    }

    const dateDic = globalThis?.dic?.date?.mask || {};
    const dateFormat = dateDic.l18n || DEFAULT_DATE_SETTINGS.l18n;
    const dateTimeFormat = dateDic.dt_l18n || `${dateFormat} - H:i`;
    const rawDate = String(date);
    const hasExplicitTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(rawDate);
    const isoMatch = hasExplicitTimezone
      ? null
      : rawDate.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
    const dateObj = isoMatch ? null : new Date(date);
    if (!isoMatch && isNaN(dateObj)) return 'NULL';
    const day = isoMatch ? isoMatch[3] : String(dateObj.getDate()).padStart(2, '0');
    const month = isoMatch ? isoMatch[2] : String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = isoMatch ? isoMatch[1] : String(dateObj.getFullYear());
    const shortYear = year.slice(2);
    const hour = isoMatch ? isoMatch[4] : String(dateObj.getHours()).padStart(2, '0');
    const minute = isoMatch ? isoMatch[5] : String(dateObj.getMinutes()).padStart(2, '0');
    const second = isoMatch ? (isoMatch[6] || '00') : String(dateObj.getSeconds()).padStart(2, '0');
    const tokens = { d: day, m: month, y: shortYear, Y: year, H: hour, i: minute, s: second };
    const formatted = dateTimeFormat.replace(/[dmyYHis]/g, (token) => tokens[token]);
    return seconds && !dateTimeFormat.includes('s') ? `${formatted}:${second}` : formatted;
  }

  /**
   * Formata uma data no padrão DB
   * 
   * @param {string} date
   * @return {string}
   */
  dateDB(date) {

    if (date && date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } else if (!date || typeof date !== 'string' || date.length === 0) {
      return date;
    }

    date = date.replace(/[\.\/]/g, '-');
    if (date.length > 10) {
      date = date.substr(0, 10);
    }

    const dateDic = globalThis?.dic?.date?.mask || {};
    const format = String(dateDic.l18n || DEFAULT_DATE_SETTINGS.l18n).split(/[^A-Za-z0-9]/);
    const split = date.split('-');

    const temp = {
      [format[0]]: split[0],
      [format[1]]: split[1],
      [format[2]]: split[2]
    }

    const [d, m, y] = [temp.d, temp.m, temp.y || temp.Y];
    return `${y}-${m}-${d}`;
  }

  /**
   * Normaliza um texto
   * @param {string} text 
   * @returns {string}
   */
  normalize(text) {
    return text
      .normalize('NFD')                   // separa acentos
      .replace(/[\u0300-\u036f]/g, '')    // remove acentos
      .replace(/\s+/g, '_')               // troca espaços por _
      .replace(/[^\w\-]/g, '')            // remove símbolos indesejados
      .toLowerCase();                     // tudo minúsculo
  }

  /**
   * Retorna um número formatado
   * 
   * @param {number} number
   * @param {object} options
   * @returns {string}
   */
  number(input, options) {
    if (input === undefined || input === null || isNaN(Number(String(input).replace(/[^0-9.,-]/g, '').replace(',', '.')))) {
      return '';
    }

    const numberDic = globalThis?.dic?.number || {};

    if (options === undefined) {
      options = {};
    }

    if (options.decimalPlaces === undefined) {
      options.decimalPlaces = numberDic.decimalPlaces ?? DEFAULT_NUMBER_SETTINGS.decimalPlaces;
    }

    if (options.integer === true || options.decimalPlaces === 0) {
      options.decimalPlaces = 0;
    }

    const settings = {
      decimalPoint: numberDic.decimalPoint ?? DEFAULT_NUMBER_SETTINGS.decimalPoint,
      thousandSeparator: numberDic.thousandSeparator ?? DEFAULT_NUMBER_SETTINGS.thousandSeparator,
      thousandPlaces: numberDic.thousandPlaces ?? DEFAULT_NUMBER_SETTINGS.thousandPlaces,
      currencySymbol: numberDic.currencySymbol ?? DEFAULT_NUMBER_SETTINGS.currencySymbol,
      ...options
    };

    let number = parseFloat(
      String(input)
        .replace(/[^0-9.,-]/g, '')
        .replace(',', '.')
    );

    const isNegative = number < 0;
    number = Math.abs(number).toFixed(settings.decimalPlaces);

    let [intPart, decimalPart] = number.split('.');
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, settings.thousandSeparator);

    let formatted = intPart;
    if (settings.decimalPlaces > 0 && decimalPart) {
      formatted += settings.decimalPoint + decimalPart;
    }

    if (isNegative) {
      formatted = '-' + formatted;
    }

    if (settings.currency === true) {
      formatted = settings.currencySymbol + ' ' + formatted;
    } else if (settings.percent === true) {
      formatted += '%';
    }

    return formatted;
  }

  /**
   * Retorna um número em formato DB
   * 
   * @param {number} number
   * @returns {string}
   */
  numberDB(number) {
    const numberDic = globalThis?.dic?.number || {};
    const thousandSeparator = numberDic.thousandSeparator ?? DEFAULT_NUMBER_SETTINGS.thousandSeparator;
    const decimalPoint = numberDic.decimalPoint ?? DEFAULT_NUMBER_SETTINGS.decimalPoint;
    const re = new RegExp(`[${escapeRegExp(thousandSeparator)}]`, 'g');
    let n = String(number ?? '').replace(re, '');
    if (decimalPoint) {
      n = n.replace(decimalPoint, '.');
    }

    return n;
  }

  /**
   * Formata horário no padrão h:i:s
   * 
   * @param {string} time
   * @returns {string}
   */
  time(time, seconds) {
    if (seconds !== true) {
      seconds = false;
    }

    if (time === null || time === undefined) {
      return time;
    }

    time = time.replace(/-/g, '/');
    if (time.length > 19) {
      time = time.substr(0, 19);
    }

    let d = new Date(time);
    let h = d.getHours().toString();
    let m = d.getMinutes().toString();
    let s = d.getSeconds().toString();

    if (h.length === 1) {
      h = '0' + h;
    }

    if (m.length === 1) {
      m = '0' + m;
    }

    if (s.length === 1) {
      s = '0' + s;
    }

    time = h + ':' + m;

    if (seconds === true) {
      time += ':' + s;
    }

    return time;
  }
}
